/**
 * Órdenes de Pedido — listado, filtros y seguimiento de estado.
 * El alta vive en NuevaOrdenPedido.jsx (alineado a FacturaCredito).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  ReceiptLong as ReceiptLongIcon,
  LockOutlined as LockOutlinedIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import VistaFactura from '../facturapos/VistaFactura';
import NuevaOrdenPedido, { PROGRAMA_ORDEN_PEDIDO } from './NuevaOrdenPedido';

export { PROGRAMA_ORDEN_PEDIDO };

const TIPO_DOC_ORDEN = 'ORDEN DE PEDIDO';

/** Estados de seguimiento editables (después de crear la orden). */
const ESTADOS_SEGUIMIENTO = [
  { value: 'SOLICITADA', label: 'Solicitadas' },
  { value: 'EN_PROCESO', label: 'En proceso' },
  { value: 'SELECCIONADA', label: 'Seleccionadas' },
  { value: 'PARA_FACTURAR', label: 'Para Facturar' },
];

const ESTADOS_TERMINALES = new Set(['FACTURADA', 'CERRADA']);

const normalizarEstado = (estado) => {
  const e = String(estado || 'SOLICITADA').trim().toUpperCase();
  if (e === 'ACTIVA' || e === 'IMPORTADA') return 'SOLICITADA';
  return e;
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const estadoChip = (estado) => {
  const e = normalizarEstado(estado);
  const map = {
    SOLICITADA: { label: 'Solicitada', color: 'default' },
    EN_PROCESO: { label: 'En proceso', color: 'info' },
    SELECCIONADA: { label: 'Seleccionada', color: 'warning' },
    PARA_FACTURAR: { label: 'Para Facturar', color: 'success' },
    FACTURADA: { label: 'Facturada', color: 'primary' },
    CERRADA: { label: 'Cerrada', color: 'default' },
  };
  const cfg = map[e] || { label: e, color: 'default' };
  return <Chip size="small" label={cfg.label} color={cfg.color} sx={{ fontWeight: 700 }} />;
};

const OrdenDePedido = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_ORDEN_PEDIDO),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_ORDEN_PEDIDO),
    [roleDetalles]
  );

  const hoy = new Date().toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(hoy.slice(0, 8) + '01');
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [nombreCliente, setNombreCliente] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const [ordenes, setOrdenes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [actualizandoEstadoId, setActualizandoEstadoId] = useState(null);
  const [dialogoNueva, setDialogoNueva] = useState(false);
  const [ordenIdEdicion, setOrdenIdEdicion] = useState(null);
  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});
  const [cargandoImprimirId, setCargandoImprimirId] = useState(null);

  const obtenerConfiguracionParaTipo = useCallback(
    async (tipoDoc) => {
      if (!selectedCompany?.db_empresa) return null;
      const nombreTipoTrim = (tipoDoc || '').trim();
      const intentos = [];
      if (nombreTipoTrim) intentos.push(nombreTipoTrim);
      const upper = nombreTipoTrim.toUpperCase();
      if (upper && !intentos.includes(upper)) intentos.push(upper);
      for (const fallback of [TIPO_DOC_ORDEN, 'FACTURA CREDITO', 'FACTURA CONTADO', 'Factura']) {
        if (!intentos.includes(fallback)) intentos.push(fallback);
      }
      let ultimaRespuesta = null;
      for (const intento of intentos) {
        try {
          const response = await fetch(`${servidor}/api/facturabackend_obtenerconfiguracion`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: intento,
            }),
          });
          if (!response.ok) continue;
          const data = await response.json();
          ultimaRespuesta = data?.configuracion || ultimaRespuesta;
          if (data?.configuracionGuardada && data?.configuracion) {
            return data.configuracion;
          }
        } catch {
          /* intento siguiente */
        }
      }
      return ultimaRespuesta;
    },
    [servidor, selectedCompany, usuarioLog, claveLog]
  );

  const armarDatosVistaOrden = useCallback(
    (orden, itemsRaw, configuracion) => {
      const items = (itemsRaw || []).map((it, idx) => ({
        id: it.id || idx,
        codigo: it.codigo,
        descripcion: it.descripcion,
        cantidad: parseFloat(it.cantidad) || 0,
        precio: parseFloat(it.precio) || 0,
        descuento: parseFloat(it.descuento) || 0,
        impuesto: parseFloat(it.impuesto) || 0,
        subtotal: parseFloat(it.subtotal) || 0,
      }));
      const totalDescuentos = items.reduce((s, i) => s + (i.descuento || 0), 0);
      const totalImpuestos =
        parseFloat(orden.impuestos) ||
        items.reduce((s, i) => s + (i.impuesto || 0), 0);
      return {
        items,
        impuestos:
          totalImpuestos > 0 ? [{ nombre: 'Impuestos', monto: totalImpuestos }] : [],
        subtotal: parseFloat(orden.subtotal) || 0,
        totalDescuentos,
        totalImpuestos,
        total: parseFloat(orden.total) || 0,
        documento: '',
        numero: String(orden.numero || ''),
        fecha: String(orden.fecha || '').slice(0, 10),
        ncf: '',
        tipoDocumento: TIPO_DOC_ORDEN,
        tipo_documento: TIPO_DOC_ORDEN,
        tipo_factura: TIPO_DOC_ORDEN,
        observaciones: orden.observaciones || '',
        empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
        id_empresa: selectedCompany,
        cliente_nombre: orden.cliente || '',
        configuracion: configuracion || undefined,
      };
    },
    [selectedCompany]
  );

  const imprimirOrden = useCallback(
    async (ordenId) => {
      if (!usuarioLog) {
        showError('Sesión inválida: falta usuario de login');
        return;
      }
      if (!ordenId) return;
      setCargandoImprimirId(ordenId);
      try {
        const response = await fetch(`${servidor}/api/ordenesdepedidos_obtener`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            orden_id: ordenId,
          }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          showError(err.Error || 'No se pudo cargar la orden para imprimir');
          return;
        }
        const data = await response.json();
        const orden = data.orden || {};
        const configuracionImpresion = await obtenerConfiguracionParaTipo(TIPO_DOC_ORDEN);
        setDatosVistaFactura(armarDatosVistaOrden(orden, data.items || [], configuracionImpresion));
        setVistaFacturaAbierta(true);
      } catch {
        showError('Error de conexión al imprimir la orden');
      } finally {
        setCargandoImprimirId(null);
      }
    },
    [
      usuarioLog,
      servidor,
      selectedCompany,
      claveLog,
      showError,
      obtenerConfiguracionParaTipo,
      armarDatosVistaOrden,
    ]
  );

  const cargarOrdenes = useCallback(async () => {
    if (!permisoLeer || !usuarioLog) return;
    setCargando(true);
    try {
      const response = await fetch(`${servidor}/api/ordenesdepedidos_listar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nombre_cliente: nombreCliente,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          estado: estadoFiltro,
          busqueda,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setOrdenes(data.ordenes || []);
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'No se pudieron cargar las órdenes');
        setOrdenes([]);
      }
    } catch {
      showError('Error de conexión al cargar órdenes');
      setOrdenes([]);
    } finally {
      setCargando(false);
    }
  }, [
    permisoLeer,
    usuarioLog,
    claveLog,
    servidor,
    selectedCompany,
    nombreCliente,
    fechaDesde,
    fechaHasta,
    estadoFiltro,
    busqueda,
    showError,
  ]);

  useEffect(() => {
    if (permisoLeer) cargarOrdenes();
  }, [permisoLeer]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirNuevaOrden = () => {
    if (!permisoModificar) {
      showError('No tiene permiso para crear órdenes de pedido');
      return;
    }
    setOrdenIdEdicion(null);
    setDialogoNueva(true);
  };

  const abrirEditarOrden = (orden) => {
    setOrdenIdEdicion(orden.id);
    setDialogoNueva(true);
  };

  const cerrarOrden = async (ordenId) => {
    if (!permisoModificar) {
      showError('No tiene permiso para cerrar órdenes');
      return;
    }
    try {
      const response = await fetch(`${servidor}/api/ordenesdepedidos_cerrar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          orden_id: ordenId,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'No se pudo cerrar la orden');
        return;
      }
      showSuccess('Orden cerrada');
      await cargarOrdenes();
    } catch {
      showError('Error de conexión al cerrar la orden');
    }
  };

  const cambiarEstadoOrden = async (ordenId, nuevoEstado) => {
    if (!permisoModificar) {
      showError('No tiene permiso para cambiar el estado de la orden');
      return;
    }
    const estado = String(nuevoEstado || '').toUpperCase();
    if (!ESTADOS_SEGUIMIENTO.some((e) => e.value === estado)) {
      showError('Estado no válido');
      return;
    }
    setActualizandoEstadoId(ordenId);
    try {
      const response = await fetch(`${servidor}/api/ordenesdepedidos_actualizarestado`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          orden_id: ordenId,
          estado,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'No se pudo actualizar el estado');
        return;
      }
      setOrdenes((prev) =>
        prev.map((o) => (o.id === ordenId ? { ...o, estado } : o))
      );
      const label = ESTADOS_SEGUIMIENTO.find((e) => e.value === estado)?.label || estado;
      showSuccess(`Estado actualizado: ${label}`);
    } catch {
      showError('Error de conexión al actualizar el estado');
    } finally {
      setActualizandoEstadoId(null);
    }
  };

  if (!permisoLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Órdenes de Pedido" subtitle="Gestión de pedidos de venta" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>Acceso denegado</Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5 }}>
              No tienes permisos para acceder a órdenes de pedido.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Órdenes de Pedido"
        subtitle="Filtros · seguimiento · nueva orden"
        cta={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirNuevaOrden}
            disabled={!permisoModificar}
            sx={{
              bgcolor: FP.color.navy,
              color: '#FFF',
              borderRadius: 1.4,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Nueva orden
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {!permisoModificar && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tiene acceso de solo lectura. No podrá crear ni cerrar órdenes.
          </Alert>
        )}

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <SearchIcon sx={{ color: FP.color.accentBlue, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="date"
                  label="Desde"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="date"
                  label="Hasta"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Cliente"
                  value={nombreCliente}
                  onChange={(e) => setNombreCliente(e.target.value)}
                  fullWidth
                  placeholder="Buscar por nombre…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Estado"
                  value={estadoFiltro}
                  onChange={(e) => setEstadoFiltro(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {ESTADOS_SEGUIMIENTO.map((e) => (
                    <MenuItem key={e.value} value={e.value}>
                      {e.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="FACTURADA">Facturada</MenuItem>
                  <MenuItem value="CERRADA">Cerrada</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Buscar"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  fullWidth
                  placeholder="Nº / vendedor…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={cargarOrdenes}
                    disabled={cargando}
                    sx={{
                      height: 40,
                      bgcolor: FP.color.navy,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1.4,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                    }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() => {
                      setNombreCliente('');
                      setBusqueda('');
                      setEstadoFiltro('');
                      setFechaDesde(hoy.slice(0, 8) + '01');
                      setFechaHasta(hoy);
                    }}
                    sx={{
                      height: 40,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1.4,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                    }}
                  >
                    Limpiar
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <ReceiptLongIcon sx={{ color: FP.color.navy, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Órdenes encontradas
            </Typography>
            <Chip
              size="small"
              label={`${ordenes.length}`}
              sx={{ fontWeight: 700, bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue }}
            />
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: 520,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: 'auto',
              m: { xs: 1.5, md: 2 },
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 1020 }}>
              <colgroup>
                {['8%', '9%', '17%', '11%', '9%', '9%', '17%', '20%'].map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Número
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                      Fecha
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Cliente
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Vendedor
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Condición
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Estado
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                      Acciones
                    </Typography>
                  </TableCell>
                </TableRow>
                {cargando ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : ordenes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: FP.color.textSecondary }}>
                      No hay órdenes con los filtros aplicados
                    </TableCell>
                  </TableRow>
                ) : (
                  ordenes.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>#{o.numero}</TableCell>
                      <TableCell align="center">{String(o.fecha || '').slice(0, 10)}</TableCell>
                      <TableCell
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={o.cliente || ''}
                      >
                        {o.cliente}
                      </TableCell>
                      <TableCell
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={o.vendedor || ''}
                      >
                        {o.vendedor || '—'}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {(o.tipo_factura || '').replace('FACTURA ', '') || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">${fmtMoney(o.total)}</TableCell>
                      <TableCell
                        sx={{
                          py: 0.75,
                          verticalAlign: 'middle',
                        }}
                      >
                        {ESTADOS_TERMINALES.has(normalizarEstado(o.estado)) || !permisoModificar ? (
                          estadoChip(o.estado)
                        ) : (
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={normalizarEstado(o.estado)}
                            disabled={actualizandoEstadoId === o.id}
                            onChange={(e) => cambiarEstadoOrden(o.id, e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: 32,
                                minHeight: 32,
                                borderRadius: 1.2,
                                bgcolor: '#FFFFFF',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: FP.color.textPrimary,
                                '& fieldset': { borderColor: FP.color.border },
                                '&:hover fieldset': { borderColor: FP.color.borderStrong },
                                '&.Mui-focused fieldset': { borderColor: FP.color.navy },
                              },
                              '& .MuiSelect-select': {
                                py: 0.5,
                                px: 1,
                                display: 'flex',
                                alignItems: 'center',
                              },
                              '& .MuiSelect-icon': {
                                fontSize: 18,
                                right: 4,
                              },
                            }}
                          >
                            {ESTADOS_SEGUIMIENTO.map((e) => (
                              <MenuItem
                                key={e.value}
                                value={e.value}
                                sx={{ fontSize: '0.8rem', fontWeight: 600, minHeight: 36 }}
                              >
                                {e.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          {!ESTADOS_TERMINALES.has(normalizarEstado(o.estado)) && (
                            <Tooltip title="Modificar orden">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => abrirEditarOrden(o)}
                                  disabled={!permisoModificar || !usuarioLog}
                                  sx={{
                                    color: FP.color.navy,
                                    '&:hover': { bgcolor: FP.color.surfaceAlt },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Imprimir orden">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => imprimirOrden(o.id)}
                                disabled={cargandoImprimirId === o.id || !usuarioLog}
                                sx={{
                                  color: FP.color.accentBlue,
                                  '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                  '&.Mui-disabled': { color: FP.color.textMuted },
                                }}
                              >
                                {cargandoImprimirId === o.id ? (
                                  <CircularProgress size={18} sx={{ color: FP.color.accentBlue }} />
                                ) : (
                                  <PrintIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {!ESTADOS_TERMINALES.has(normalizarEstado(o.estado)) && permisoModificar ? (
                            <Button
                              size="small"
                              onClick={() => cerrarOrden(o.id)}
                              sx={{ textTransform: 'none', fontWeight: 700, color: FP.color.textSecondary }}
                            >
                              Cerrar
                            </Button>
                          ) : o.nfact ? (
                            <Typography sx={{ fontSize: '0.75rem', color: FP.color.textMuted }}>
                              Fact. {o.nfact}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <NuevaOrdenPedido
        open={dialogoNueva}
        ordenId={ordenIdEdicion}
        onClose={() => { setDialogoNueva(false); setOrdenIdEdicion(null); }}
        onGuardado={(data) => {
          const eraNew = !ordenIdEdicion;
          setDialogoNueva(false);
          setOrdenIdEdicion(null);
          cargarOrdenes();
          if (eraNew && data?.orden_id) imprimirOrden(data.orden_id);
        }}
      />

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={() => setVistaFacturaAbierta(false)}
        facturaData={datosVistaFactura}
      />
    </Box>
  );
};

export default OrdenDePedido;
